/**
 * ImpersonationBanner — renders above AppShell + AdminShell when a super
 * admin is impersonating. Host-branched copy (Batch 21a-3 §7, Security
 * BLOCKER #16):
 *
 *   Admin host — full detail (target email + slug + expires) + Stop CTA.
 *     Sources from the existing ``useImpersonationStore`` (populated from
 *     GET /admin/impersonate/status).
 *
 *   App host — role-only attribution ("support_staff"); no identity, no
 *     Stop CTA (tenant users cannot end a super-admin session). Sources
 *     from GET /auth/status — the only app-host-safe status endpoint.
 */
import { detectHost } from "@/lib/host";
import { useAuthStatus } from "@/hooks/queries/auth/useAuthStatus";
import { useImpersonationStore } from "@/stores/useImpersonationStore";

export function ImpersonationBanner(): JSX.Element {
  const host = detectHost();
  const adminStore = useImpersonationStore();
  const { data: authStatus } = useAuthStatus();

  if (host === "admin") {
    if (!adminStore) return <></>;
    const targetLabel =
      adminStore.targetTenantSlug ?? adminStore.targetTenantId ?? "unknown";
    return (
      <div
        role="alert"
        className="sticky top-0 z-50 bg-yellow-400 px-4 py-2 text-sm font-medium text-text-primary"
      >
        Impersonating tenant {targetLabel}
        {adminStore.reason ? ` — ${adminStore.reason}` : ""}
      </div>
    );
  }

  // App host — role-only attribution.
  if (!authStatus?.is_impersonating) return <></>;
  return (
    <div
      role="alert"
      className="sticky top-0 z-50 bg-yellow-400 px-4 py-2 text-center text-sm font-medium text-text-primary"
    >
      You are being assisted by support staff.
    </div>
  );
}
